import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight, Sparkles, Smartphone, Zap, RefreshCw, ShieldCheck,
  Truck, Star, Leaf, Gem, CreditCard, BarChart3, Calendar, Check,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { PRODUCTS, productBySlug, formatMoney, getProductBasePrice } from "@/lib/ecom/catalog";
import { useCurrency } from "@/lib/ecom/currency";
import metalHero from "@/assets/products/custom-black-metal.webp";
import bambooHero from "@/assets/products/bamboo-hero.webp";
import whiteHero from "@/assets/products/white-hero.webp";
import blackHero from "@/assets/products/custom-black-pvc.webp";
import goldHero from "@/assets/products/gold-hero.webp";
import googleHero from "@/assets/products/google-review-hero.webp";

const HERO_OG = metalHero;

const PRODUCT_IMAGES: Record<string, string> = {
  metal: metalHero,
  bamboo: bambooHero,
  "white-pvc": whiteHero,
  "black-pvc": blackHero,
  "24kt-gold": goldHero,
  "google-review": googleHero,
};

export const Route = createFileRoute("/shop")({
  head: () => ({
    meta: [
      { title: "Shop NFC Business Cards — Metal, Bamboo & Gold | Banana" },
      { name: "description", content: "Premium NFC business cards in metal, bamboo, 24kt gold and PVC — plus Google Review cards. One tap shares everything." },
      { property: "og:title", content: "Shop NFC Business Cards — Metal, Bamboo & Gold | Banana" },
      { property: "og:description", content: "Premium AI NFC business cards. Pick your material — tap to share, never lose a contact again." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://banana.cards/shop" },
      { property: "og:image", content: HERO_OG },
      { property: "twitter:card", content: "summary_large_image" },
      { property: "twitter:image", content: HERO_OG },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/shop" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Store",
          name: "Banana Cards Shop",
          url: "https://banana.cards/shop",
          description: "Premium AI-powered NFC business cards and Google Review cards.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "ItemList",
          name: "Banana Cards Shop — NFC Business Cards",
          numberOfItems: PRODUCTS.length,
          itemListElement: PRODUCTS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://banana.cards${p.href}`,
            item: {
              "@type": "Product",
              "@id": `https://banana.cards${p.href}#product`,
              name: p.name,
              sku: p.sku,
              mpn: p.sku,
              brand: { "@type": "Brand", name: "Banana Cards" },
              category: "Business Cards",
              image: PRODUCT_IMAGES[p.slug]
                ? `https://banana.cards${PRODUCT_IMAGES[p.slug]}`
                : undefined,
              url: `https://banana.cards${p.href}`,
              offers: {
                "@type": "Offer",
                priceCurrency: "USD",
                price: p.prices.US,
                availability: "https://schema.org/InStock",
                url: `https://banana.cards${p.href}`,
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://banana.cards/" },
            { "@type": "ListItem", position: 2, name: "Shop", item: "https://banana.cards/shop" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": "https://banana.cards/shop#collection",
          name: "NFC Business Cards Collection",
          url: "https://banana.cards/shop",
          description: "Premium NFC business cards in metal, bamboo, 24kt gold, PVC and Google Review formats.",
          isPartOf: { "@type": "WebSite", name: "Banana Cards", url: "https://banana.cards/" },
          about: { "@id": "https://banana.cards/shop#collection-items" },
        }),
      },
    ],
  }),
  component: ShopIndex,
});

type CardCopy = {
  slug: string;
  image: string;
  tagline: string;
  blurb: string;
  badge?: string;
  accent: string;
  bullets: string[];
  Icon: typeof Sparkles;
};

const COPY: CardCopy[] = [
  {
    slug: "metal",
    image: metalHero,
    tagline: "Black powder-coated stainless steel",
    blurb:
      "Engineered for impact. Laser-engraved logo, embedded NFC, lifetime guarantee.",
    badge: "Bestseller",
    accent: "bg-foreground text-background",
    bullets: ["Stainless steel core", "Laser-engraved logo", "Lifetime guarantee"],
    Icon: Gem,
  },
  {
    slug: "24kt-gold",
    image: goldHero,
    tagline: "24kt gold plated, brand-defining",
    blurb:
      "The flex card. Real 24kt gold plating with custom engraving and AI follow-ups built in.",
    badge: "Premium",
    accent: "bg-[oklch(0.82_0.16_85)] text-black",
    bullets: ["24kt gold plating", "Hand-finished", "Concierge onboarding"],
    Icon: Sparkles,
  },
  {
    slug: "bamboo",
    image: bambooHero,
    tagline: "Sustainably-sourced bamboo",
    blurb:
      "The thoughtful option. Warm, natural and 100% biodegradable — without losing any features.",
    badge: "Eco",
    accent: "bg-brand-lime text-brand-lime-foreground",
    bullets: ["Sustainable bamboo", "Hand-engraved", "Plant-a-tree program"],
    Icon: Leaf,
  },
  {
    slug: "white-pvc",
    image: whiteHero,
    tagline: "Glossy white PVC, brand-first",
    blurb:
      "The clean canvas. Full-color print, your logo big and bright — at a startup-friendly price.",
    badge: "Best value",
    accent: "bg-background text-foreground border border-foreground/15",
    bullets: ["Full-color print", "Lightweight", "Apple & Google Wallet"],
    Icon: CreditCard,
  },
  {
    slug: "black-pvc",
    image: blackHero,
    tagline: "Matte black PVC, sleek & modern",
    blurb:
      "The stealth pick. Deep black finish with crisp print — a confident, low-key flex.",
    badge: "Bestseller",
    accent: "bg-foreground text-background",
    bullets: ["Matte black finish", "Full-color print", "Apple & Google Wallet"],
    Icon: CreditCard,
  },
  {
    slug: "google-review",
    image: googleHero,
    tagline: "5-star reviews on demand",
    blurb:
      "The growth card. Tap on the counter, customer leaves a review — that simple.",
    badge: "For local businesses",
    accent: "bg-blue-500 text-white",
    bullets: ["Direct to Google review", "No app needed", "Track every scan"],
    Icon: Star,
  },
];

function getCopy(slug: string) {
  return COPY.find((c) => c.slug === slug)!;
}

export default function ShopIndex() {
  const { country } = useCurrency();
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-60">
          <div className="absolute -top-24 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-brand-lime/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-16 md:px-6 md:pb-16 md:pt-24">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <Sparkles className="h-3 w-3" /> The collection
            </span>
            <h1 className="mt-5 max-w-4xl font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
              Pick your card.<br className="hidden md:block" />
              <span className="italic text-muted-foreground">Tap into everything else.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-balance text-[15px] leading-relaxed text-muted-foreground md:text-base">
              Five materials. One platform. Every Banana Card pairs premium hardware with the
              banana.cards AI profile, CRM, bookings and follow-ups — out of the box, no app required.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a
                href="#collection"
                className="inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90"
              >
                Browse the collection <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-xl border border-foreground/15 px-6 py-3 text-sm font-bold text-foreground transition hover:bg-muted"
              >
                See platform plans
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Free worldwide shipping over $50</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Lifetime hardware guarantee</span>
              <span className="inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> 4.9 · 2,100+ reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTION */}
      <section id="collection" className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-20">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">All cards</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">The full Banana Cards lineup</h2>
          </div>
          <p className="hidden max-w-sm text-sm text-muted-foreground md:block">
            Every card ships ready to tap — paired to your free banana.cards profile in under 60 seconds.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {COPY.map((c, i) => {
            const p = productBySlug(c.slug);
            if (!p) return null;
            const fromPrice = getProductBasePrice(p.slug, country.code);
            return (
              <Link
                key={c.slug}
                to={p.href as "/shop/metal"}
                className="group relative flex flex-col overflow-hidden rounded-3xl bg-card transition hover:-translate-y-0.5"
              >
                <div className="relative aspect-[5/4] overflow-hidden bg-background">
                  <img
                    src={c.image}
                    alt={p.name}
                    loading={i < 2 ? "eager" : "lazy"}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                  />
                  {c.badge && (
                    <span className={`absolute left-4 top-4 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${c.accent}`}>
                      <c.Icon className="h-3 w-3" /> {c.badge}
                    </span>
                  )}
                  <span className="absolute right-4 top-4 rounded-full bg-background/90 px-3 py-1 text-[11px] font-bold tabular-nums text-foreground backdrop-blur">
                    From {formatMoney(fromPrice, country)}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{c.tagline}</p>
                  <h3 className="mt-1.5 font-display text-2xl leading-tight md:text-3xl">{p.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.blurb}</p>

                  <ul className="mt-4 space-y-1.5">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[13px] text-foreground/80">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex items-center justify-between border-t border-foreground/10 pt-4">
                    <span className="text-sm font-semibold text-foreground">Shop {p.shortName}</span>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background transition group-hover:translate-x-0.5">
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">How it works</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">From unboxing to first tap in 60 seconds</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Pick & personalize", d: "Choose your material, upload your logo, and we craft the card.", Icon: Sparkles },
              { n: "02", t: "Tap to activate", d: "Tap once on your phone to claim your free banana.cards profile.", Icon: Smartphone },
              { n: "03", t: "Share & follow up", d: "Tap to share. AI captures every contact and writes the follow-up.", Icon: Zap },
            ].map((s) => (
              <div key={s.n} className="rounded-3xl bg-muted p-6">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Step {s.n}</span>
                  <s.Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <h3 className="mt-3 font-display text-2xl leading-tight">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mb-10 flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Compare materials</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Which Banana Card is right for you?</h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            Every card runs the same AI platform. The difference is the first impression.
          </p>
        </div>

        <div className="overflow-x-auto rounded-3xl bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-foreground/10 text-left text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                <th className="px-5 py-3">Card</th>
                <th className="px-5 py-3">Best for</th>
                <th className="px-5 py-3">Feel</th>
                <th className="px-5 py-3">From</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {[
                { slug: "metal",         best: "Founders, sales pros", feel: "Heavy, premium, cold-touch" },
                { slug: "24kt-gold",     best: "Luxury, hospitality",  feel: "Showpiece, mirror finish"   },
                { slug: "bamboo",        best: "Sustainable brands",   feel: "Warm, natural, light"       },
                { slug: "white-pvc",     best: "Teams & startups",     feel: "Clean, full-color print"    },
                { slug: "google-review", best: "Local businesses",     feel: "Bright PVC, counter-ready"  },
              ].map((row) => {
                const p = productBySlug(row.slug)!;
                return (
                  <tr key={row.slug} className="border-b border-foreground/10 last:border-0">
                    <td className="px-5 py-4 font-semibold">{p.name}</td>
                    <td className="px-5 py-4 text-muted-foreground">{row.best}</td>
                    <td className="px-5 py-4 text-muted-foreground">{row.feel}</td>
                    <td className="px-5 py-4 tabular-nums">{formatMoney(getProductBasePrice(p.slug, country.code), country)}</td>
                    <td className="px-5 py-4 text-right">
                      <Link to={p.href as "/shop/metal"} className="inline-flex items-center gap-1 text-sm font-bold text-foreground hover:underline">
                        Shop <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      </section>

      {/* PLATFORM STRIP */}
      <section className="relative overflow-hidden bg-brand-dark text-background">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-12 md:px-6 md:py-24">
          <div className="md:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-background/70">
              Every card includes
            </p>
            <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              An AI platform, not just a card.
            </h2>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-background/80">
              Your card is the trigger. Behind it is a CRM, an AI follow-up engine, smart bookings,
              contract e-signing and team analytics — all included from day one.
            </p>
            <Link
              to="/pricing"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-brand-lime px-5 py-2.5 text-sm font-bold text-brand-lime-foreground transition hover:opacity-90"
            >
              See what's included <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {[
                { t: "AI profile",         d: "Multi-language, auto-updated", Icon: Sparkles },
                { t: "Contact capture",    d: "Every tap, never lost",        Icon: RefreshCw },
                { t: "AI follow-ups",      d: "Drafted in seconds",           Icon: Zap },
                { t: "Bookings",           d: "Calendar & video included",    Icon: Calendar },
                { t: "Analytics",          d: "Track every interaction",      Icon: BarChart3 },
                { t: "Team controls",      d: "Invite, brand, govern",        Icon: ShieldCheck },
              ].map((f) => (
                <div key={f.t} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <f.Icon className="h-4 w-4 text-brand-lime" />
                  <p className="mt-3 text-sm font-bold">{f.t}</p>
                  <p className="mt-0.5 text-[12px] text-background/70">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid gap-6 md:grid-cols-4">
          {[
            { n: "2,100+",  l: "Cards in the wild" },
            { n: "4.9 / 5", l: "Customer rating" },
            { n: "60 sec",  l: "Average setup time" },
            { n: "120+",    l: "Countries shipped" },
          ].map((s) => (
            <div key={s.l} className="rounded-3xl bg-muted p-6 text-center">
              <p className="font-display text-4xl">{s.n}</p>
              <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.l}</p>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">FAQ</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Questions about the cards</h2>
          </div>
          <div className="space-y-3">
            {[
              { q: "Do I need an app to share my card?", a: "No. NFC works natively on every iOS and Android phone made in the last decade. Tap once — your full profile opens in the browser." },
              { q: "Can I order one card to try, then upgrade later?", a: "Yes. Start with a single card to test, then bundle 3, 5 or 10 at a discount when you're ready to roll out to the team." },
              { q: "Are the cards reprogrammable?", a: "Your card never expires and the data on it can be updated anytime from your dashboard — no need to reprint." },
              { q: "How long does shipping take?", a: "Custom cards ship in 3–5 business days. Free worldwide shipping on orders over $50." },
              { q: "What if I lose my card?", a: "Deactivate it instantly from your dashboard and order a replacement at a member discount. Your contacts and profile stay safe." },
            ].map((f) => (
              <details key={f.q} className="group rounded-2xl bg-card p-5 open:shadow-sm">
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-left text-[15px] font-semibold">
                  {f.q}
                  <span className="ml-4 inline-flex h-7 w-7 items-center justify-center rounded-full border border-foreground/15 text-muted-foreground transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-foreground p-8 text-background md:p-14">
          <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-brand-lime/40 blur-3xl" />
          <div className="relative grid items-center gap-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-background/60">Ready when you are</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Your next first impression is one tap away.
              </h2>
              <p className="mt-4 max-w-md text-[15px] text-background/70">
                Free shipping over $50. Lifetime hardware guarantee. 30-day no-questions-asked returns.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link to="/shop/metal" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:opacity-90">
                  Shop the bestseller <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-background/10">
                  Talk to sales
                </Link>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>
    </SiteLayout>
  );
}
